import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from '../app/axiosInterceptor';
import { HTTP_GETTYPE, STATUS_API, TIMEOUT_DEFAULT } from 'src/app/constant/config';
import { MESSAGE } from 'src/app/constant/message';
import AxiosAdapter from './AxiosAdapter';

// --- Order APIs ---
export const getListOrders = AxiosAdapter.GetHttp(
  'orderSlice/getListOrders',
  '/order',
  HTTP_GETTYPE.ALL_PAGINATION
);

export const getOrderById = AxiosAdapter.HttpGetById(
  'orderSlice/getOrderById',
  '/order'
);

export const createOrder = AxiosAdapter.HttpPost(
  'orderSlice/createOrder',
  '/order',
  MESSAGE.CREATE_ORDER_SUCCESS,
  MESSAGE.CREATE_ORDER_FAIL
);

export const updateOrder = AxiosAdapter.HttpUpdateById(
  'orderSlice/updateOrder',
  '/order',
  MESSAGE.UPDATE_ORDER_SUCCESS,
  MESSAGE.UPDATE_ORDER_FAIL
);

export const updateOrderStatus = AxiosAdapter.HttpPatch(
  'orderSlice/updateOrderStatus',
  '/order',
  MESSAGE.UPDATE_ORDER_SUCCESS,
  MESSAGE.UPDATE_ORDER_FAIL
);

export const syncOrderDeviceTypes = AxiosAdapter.HttpPatch(
  'orderSlice/syncOrderDeviceTypes',
  '/order',
  MESSAGE.UPDATE_ORDER_SUCCESS,
  null
);

export const deleteOrder = AxiosAdapter.HttpDelete(
  'orderSlice/deleteOrder',
  'order',
  MESSAGE.DELETE_ORDER_SUCCESS,
  MESSAGE.DELETE_ORDER_FAIL
);

export const deleteOrderImage = AxiosAdapter.HttpDelete(
  'orderSlice/deleteOrderImage',
  'order/images',
  'Xóa ảnh thành công',
  'Xóa ảnh thất bại'
);

export const uploadOrderImages = createAsyncThunk(
  'orderSlice/uploadOrderImages',
  async ({ id, images }, { rejectWithValue }) => {
    try {
      const formData = new FormData();
      images.forEach((img) => formData.append('images', img));

      const response = await axios({
        url: `${process.env.REACT_APP_BACKEND_URL}/order/${id}/images`,
        method: 'POST',
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: 'Bearer ' + localStorage.getItem('access-token')
        },
        data: formData,
        timeout: TIMEOUT_DEFAULT
      });
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

// --- Order Device Types APIs ---
export const getOrderDeviceTypes = AxiosAdapter.HttpGetById(
  'orderSlice/getOrderDeviceTypes',
  '/order' 
);


export const getOrderDeviceTypesByOrderId = AxiosAdapter.GetHttp(
  'orderSlice/getOrderDeviceTypesByOrderId',
  '/order/',
  HTTP_GETTYPE.DETAIL
);

export const createOrderDeviceType = AxiosAdapter.GetHttp(
  'orderSlice/createOrderDeviceType',
  '/order/',
  HTTP_GETTYPE.DETAIL
);



export const getOrderDetailsByOrderId = AxiosAdapter.GetHttp(
  'orderSlice/getOrderDetailsByOrderId',
  '/order/',
  HTTP_GETTYPE.DETAIL
);

const orderSlice = createSlice({
  name: 'orderSlice',
  initialState: {
    listOrders: null,
    total: 0,
    currentOrder: null,
    listOrderDeviceTypes: [],
    listOrderDetails: [],
    error: null,
    statusGet: null,
    statusCreate: null,
    statusUpdate: null,
    statusDelete: null,
    isLoading: false,
    totalAmountSum: 0,
    totalPaidSum: 0,
    statistic: {},
  },
  reducers: {
    resetChange: state => {
      state.statusGet = null;
      state.statusCreate = null;
      state.statusUpdate = null;
      state.statusDelete = null;
      state.isLoading = false;
      state.error = null;
      state.totalAmountSum = 0;
      state.totalPaidSum = 0;
    },

    updateOrderInList: (state, action) => {
      const { id, data } = action.payload;
      if (state.listOrders) {
        const idx = state.listOrders.findIndex(o => o.id === id);
        if (idx !== -1) {
          state.listOrders[idx] = { ...state.listOrders[idx], ...data };
        }
      }
    }
  },
  extraReducers: {
    [getListOrders.pending]: state => {
      state.statusGet = STATUS_API.PENDING;
      state.isLoading = true;
    },
    [getListOrders.fulfilled]: (state, action) => {
      state.statusGet = STATUS_API.SUCCESS;

      const payload = action.payload?.payload;

      state.listOrders = payload?.orders || [];
      state.total = action.payload?.numberOfItem || action.payload?.total || payload?.total || state.listOrders.length;
      state.totalAmountSum = action.payload?.total_amount_sum || 0;
      state.totalPaidSum = action.payload?.total_paid_sum || 0;
      
      const { status, keywords, from_date, to_date, customer_type, device_type_id } = action.meta.arg || {};
      const isFiltered = status !== undefined || keywords !== undefined || from_date !== undefined || to_date !== undefined || customer_type !== undefined || device_type_id !== undefined;
      
      if (!isFiltered) {
        state.statistic = action.payload?.statistic || {};
      }
      
      state.isLoading = false;
    },
    [getListOrders.rejected]: (state, action) => {
      state.statusGet = STATUS_API.ERROR;
      state.error = action.payload?.message || action.error;
      state.isLoading = false;
    },

    [getOrderById.fulfilled]: (state, action) => {
      state.currentOrder = action.payload.payload;
    },

    [createOrder.pending]: state => {
      state.statusCreate = STATUS_API.PENDING;
    },
    [createOrder.fulfilled]: state => {
      state.statusCreate = STATUS_API.SUCCESS;
    },
    [createOrder.rejected]: (state, action) => {
      state.statusCreate = STATUS_API.ERROR;
      state.error = action.payload?.message;
    },

    [updateOrder.pending]: state => {
      state.statusUpdate = STATUS_API.PENDING;
    },
    [updateOrder.fulfilled]: state => {
      state.statusUpdate = STATUS_API.SUCCESS;
    },
    [updateOrder.rejected]: (state, action) => {
      state.statusUpdate = STATUS_API.ERROR;
      state.error = action.payload?.message;
    },

    [updateOrderStatus.pending]: state => {
      state.statusUpdate = STATUS_API.PENDING;
    },
    [updateOrderStatus.fulfilled]: state => {
      state.statusUpdate = STATUS_API.SUCCESS;
    },
    [updateOrderStatus.rejected]: (state, action) => {
      state.statusUpdate = STATUS_API.ERROR;
      state.error = action.payload?.message;
    },

    [syncOrderDeviceTypes.pending]: state => {
      state.statusUpdate = STATUS_API.PENDING;
    },
    [syncOrderDeviceTypes.fulfilled]: state => {
      state.statusUpdate = STATUS_API.SUCCESS;
    },
    [syncOrderDeviceTypes.rejected]: (state, action) => {
      state.statusUpdate = STATUS_API.ERROR;
      state.error = action.payload?.message;
    },

    [deleteOrder.pending]: state => {
      state.statusDelete = STATUS_API.PENDING;
    },
    [deleteOrder.fulfilled]: state => {
      state.statusDelete = STATUS_API.SUCCESS;
    },
    [deleteOrder.rejected]: (state, action) => {
      state.statusDelete = STATUS_API.ERROR;
      state.error = action.payload?.message;
    },
    [deleteOrderImage.pending]: state => {
      state.statusDelete = STATUS_API.PENDING;
    },
    [deleteOrderImage.fulfilled]: state => {
      state.statusDelete = STATUS_API.SUCCESS;
    },
    [deleteOrderImage.rejected]: (state, action) => {
      state.statusDelete = STATUS_API.ERROR;
      state.error = action.payload?.message;
    },

    [getOrderDeviceTypesByOrderId.fulfilled]: (state, action) => {
      state.listOrderDeviceTypes = action.payload.payload || [];
    },

    [getOrderDetailsByOrderId.fulfilled]: (state, action) => {
      state.listOrderDetails = action.payload.payload || [];
    }
  }
});

export const { resetChange, updateOrderInList } = orderSlice.actions;

export default orderSlice.reducer;
